import { Column } from '@ag-grid-community/core';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import dayjs from 'dayjs';
import { StyleDictionary, TableCell } from 'pdfmake/interfaces';

import { MainQuery } from '@shared/store/main/main.query';

import { ApiService } from './../api.service';
import { PdfExportWorkerParams } from './pdf-export.worker';
import { OverlayService } from '../overlay.service';

interface PdfExportOptions {
  columns: Column[];
  customStyles?: StyleDictionary;
  fileName: string;
  getRows: () => Promise<TableCell[][]>;
  headerRow?: TableCell[];
}

@Injectable()
export class PdfExportService implements OnDestroy {
  private apiService = inject(ApiService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  isProcessing = signal(false);

  private worker: null | Worker = null;

  ngOnDestroy() {
    this.terminateWorker();
  }

  private createWorker(): Worker {
    if (typeof Worker === 'undefined') {
      throw new Error('Web Workers are not supported in this environment.');
    }

    return new Worker(new URL('./pdf-export.worker', import.meta.url));
  }

  async exportToPdf({
    columns,
    customStyles = {},
    fileName,
    getRows,
    headerRow: extraHeaderRow = [],
  }: PdfExportOptions): Promise<void> {
    this.terminateWorker();
    this.worker = this.createWorker();

    this.isProcessing.set(true);

    const rows = await getRows();

    const headerRow = this.getHeaders(columns);

    const trialName = this.mainQuery.getSelectedTrial()?.short_name ?? '';
    const currentDateTime = dayjs().format('MMMM D, YYYY h:mm A');

    const trialRow = this.getTopHeaderRow(`Trial: ${trialName}`, columns.length);
    const timestampRows = this.getTopHeaderRow(`Generated on: ${currentDateTime}`, columns.length);

    const tableBody: TableCell[][] = extraHeaderRow.length
      ? [trialRow, timestampRows, extraHeaderRow, headerRow, ...rows]
      : [trialRow, timestampRows, headerRow, ...rows];

    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Web Workers are not supported in this environment.'));

        return;
      }

      this.worker.onmessage = ({ data: { blob } }) => {
        this.apiService.downloadBlob(blob, `${fileName}.pdf`);
        this.terminateWorker();
        this.isProcessing.set(false);
        resolve();
      };

      this.worker.onerror = (error) => {
        this.overlayService.error(`Error exporting to PDF: ${error.message}`);
        this.isProcessing.set(false);
        reject(error);
      };

      const payload: PdfExportWorkerParams = {
        columnLength: columns.length,
        customStyles,
        tableBody,
      };

      this.worker.postMessage(payload);
    });
  }

  private getHeaders(columns: Column[]) {
    const BLUE = '#094673';

    return columns.map((column) => {
      const colDef = column.getColDef();
      const headerName = colDef.headerName || '';

      return {
        borderColor: Array(4).fill(BLUE),
        style: 'tableHeader',
        text: headerName,
      } as TableCell;
    });
  }

  private getTopHeaderRow(text: string, headersCount: number) {
    return [
      {
        borderColor: Array(4).fill('gray'),
        colSpan: headersCount,
        style: 'trialHeaderRow',
        text,
      },
      ...Array(headersCount - 1).fill({}),
    ];
  }

  private terminateWorker() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
